import React, { useEffect, useState } from 'react';
import { View, Text, Modal, TouchableOpacity, StyleSheet, ScrollView, Alert, ActivityIndicator } from 'react-native';
import type { Appointment, OrderLineItem } from '../utils/appointmentManager';

interface EditOrderItemsModalProps {
  visible: boolean;
  appointment: Appointment | null;
  onClose: () => void;
  onSaved: () => void;
}

function lineItemTotal(item: OrderLineItem): number {
  const optionsModifier = (item.selectedOptions || []).reduce(
    (sum, opt) => sum + (opt.priceModifier || 0),
    0
  );
  return (item.unitPrice + optionsModifier) * item.quantity;
}

const EditOrderItemsModal: React.FC<EditOrderItemsModalProps> = ({
  visible,
  appointment,
  onClose,
  onSaved,
}) => {
  const [items, setItems] = useState<OrderLineItem[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible && appointment?.orderLineItems) {
      setItems(appointment.orderLineItems.map((i) => ({ ...i })));
    }
  }, [visible, appointment]);

  if (!visible || !appointment) {
    return null;
  }

  const originalTotal = Number(appointment.paymentData?.amount) || 0;
  const newTotal = items.reduce((sum, item) => sum + lineItemTotal(item), 0);
  const delta = Math.round((newTotal - originalTotal) * 100) / 100;

  const adjustQuantity = (itemId: string, delta: number) => {
    setItems((prev) =>
      prev
        .map((item) => (item.itemId === itemId ? { ...item, quantity: item.quantity + delta } : item))
        .filter((item) => item.quantity > 0)
    );
  };

  const removeItem = (itemId: string) => {
    setItems((prev) => prev.filter((item) => item.itemId !== itemId));
  };

  const handleSave = async () => {
    if (items.length === 0) {
      Alert.alert('No items', 'An order needs at least one item. Cancel this order instead if it should be removed entirely.');
      return;
    }

    setSaving(true);
    try {
      const { appointmentManager } = await import('../utils/appointmentManager');
      const paymentDataPatch: Record<string, unknown> = { amount: newTotal };

      if (delta < 0) {
        const refundAmount = Math.abs(delta);
        const paymentMethod = appointment.paymentData?.paymentMethod;
        const businessId = appointment.businessOwnerId;

        let refunded = false;
        let refundError: string | null = null;

        if (paymentMethod === 'paypal' && appointment.paymentData?.captureId && businessId) {
          const { refundPaypalCapture } = await import('../services/paypalOrdersService');
          const result = await refundPaypalCapture(businessId, appointment.paymentData.captureId, refundAmount);
          refunded = result.success;
          refundError = result.error || null;
        } else if (appointment.paymentData?.transactionId?.startsWith('pi_')) {
          const { refundStripePayment } = await import('../services/stripePaymentService');
          const result = await refundStripePayment(appointment.paymentData.transactionId, refundAmount);
          refunded = result.success;
          refundError = result.error || null;
        }

        if (refunded) {
          paymentDataPatch.lastRefund = {
            amount: refundAmount,
            at: new Date().toISOString(),
          };
          paymentDataPatch.underpaidAmount = null;
        } else {
          // No real charge to refund against (pay-later, unpaid, or a
          // native-app fake transaction id that Stripe/PayPal rejected) —
          // update the record only; merchant handles the refund manually.
          paymentDataPatch.needsManualRefund = refundAmount;
          console.warn('Could not auto-refund order edit:', refundError);
        }
      } else if (delta > 0) {
        paymentDataPatch.underpaidAmount = delta;
      }

      await appointmentManager.updateAppointmentOrderItems(appointment.id, items, paymentDataPatch);

      if (delta < 0 && paymentDataPatch.needsManualRefund) {
        Alert.alert(
          'Order updated',
          `Order updated, but $${Math.abs(delta).toFixed(2)} could not be automatically refunded — please refund the customer manually.`
        );
      } else if (delta > 0) {
        Alert.alert('Order updated', `Order updated. Customer now owes an additional $${delta.toFixed(2)} — collect this off-platform.`);
      }

      onSaved();
      onClose();
    } catch (error: any) {
      console.error('Error saving order edit:', error);
      Alert.alert('Error', error?.message || 'Could not save order changes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.modal}>
          <View style={styles.header}>
            <Text style={styles.headerTitle}>Edit Order</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.content}>
            {items.map((item) => (
              <View key={item.itemId} style={styles.itemRow}>
                <View style={styles.itemInfo}>
                  <Text style={styles.itemName}>{item.name}</Text>
                  <Text style={styles.itemPrice}>${lineItemTotal(item).toFixed(2)}</Text>
                  {(item.selectedOptions || []).map((opt) => (
                    <Text key={opt.optionId} style={styles.itemOption}>
                      {opt.optionName}: {opt.valueName}
                    </Text>
                  ))}
                </View>
                <View style={styles.itemActions}>
                  <View style={styles.stepper}>
                    <TouchableOpacity style={styles.stepperButton} onPress={() => adjustQuantity(item.itemId, -1)}>
                      <Text style={styles.stepperButtonText}>−</Text>
                    </TouchableOpacity>
                    <Text style={styles.stepperValue}>{item.quantity}</Text>
                    <TouchableOpacity style={styles.stepperButton} onPress={() => adjustQuantity(item.itemId, 1)}>
                      <Text style={styles.stepperButtonText}>+</Text>
                    </TouchableOpacity>
                  </View>
                  <TouchableOpacity onPress={() => removeItem(item.itemId)}>
                    <Text style={styles.removeText}>Remove</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ))}

            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>New total:</Text>
              <Text style={styles.totalValue}>${newTotal.toFixed(2)}</Text>
            </View>
            {delta !== 0 && (
              <Text style={delta < 0 ? styles.deltaNegative : styles.deltaPositive}>
                {delta < 0
                  ? `$${Math.abs(delta).toFixed(2)} will be refunded to the customer.`
                  : `Customer will owe an additional $${delta.toFixed(2)} (collected off-platform).`}
              </Text>
            )}
          </ScrollView>

          <View style={styles.footer}>
            <TouchableOpacity style={styles.cancelButton} onPress={onClose} disabled={saving}>
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.saveButton, saving && styles.saveButtonDisabled]} onPress={handleSave} disabled={saving}>
              {saving ? <ActivityIndicator color="#fff" /> : <Text style={styles.saveButtonText}>Save Changes</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: 20,
    width: '95%',
    maxWidth: 600,
    maxHeight: '85%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 10,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  closeButton: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    fontSize: 18,
    color: '#666',
  },
  content: {
    padding: 16,
  },
  itemRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    gap: 12,
  },
  itemInfo: {
    flex: 1,
  },
  itemName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#1C1C1E',
  },
  itemPrice: {
    fontSize: 14,
    color: '#666',
    marginTop: 2,
  },
  itemOption: {
    fontSize: 12,
    color: '#8E8E93',
    marginTop: 2,
  },
  itemActions: {
    alignItems: 'flex-end',
    gap: 6,
  },
  stepper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
  },
  stepperButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  stepperButtonText: {
    fontSize: 18,
    fontWeight: '700',
    color: '#007AFF',
  },
  stepperValue: {
    fontSize: 15,
    fontWeight: '600',
    minWidth: 20,
    textAlign: 'center',
  },
  removeText: {
    fontSize: 13,
    color: '#c00',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  totalLabel: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1C1C1E',
  },
  totalValue: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1C1C1E',
  },
  deltaNegative: {
    fontSize: 13,
    color: '#34C759',
    marginTop: 8,
  },
  deltaPositive: {
    fontSize: 13,
    color: '#FF9500',
    marginTop: 8,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    backgroundColor: '#f8f9fa',
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    marginRight: 10,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#666',
  },
  saveButton: {
    flex: 1,
    paddingVertical: 12,
    backgroundColor: '#007AFF',
    borderRadius: 8,
    alignItems: 'center',
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    fontSize: 16,
    color: 'white',
    fontWeight: 'bold',
  },
});

export default EditOrderItemsModal;
